import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

// Setup axios interceptor - use sessionStorage
axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('docflow_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.withCredentials = true;
  return config;
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyVerified, setKeyVerified] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(null); // temp_token + user info for 2FA step
  const sessionTimerRef = useRef(null);

  // Check existing session on mount
  useEffect(() => {
    const keyOk = sessionStorage.getItem('docflow_key_verified') === 'true';
    setKeyVerified(keyOk);
    if (keyOk) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  // Session timer: auto-logout after 1 hour
  const startSessionTimer = useCallback(() => {
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    sessionTimerRef.current = setTimeout(() => {
      fullCleanupAndLogout();
    }, 60 * 60 * 1000); // 1 hour
  }, []);

  // Cleanup on browser close/tab close
  useEffect(() => {
    const handleBeforeUnload = () => {
      const token = sessionStorage.getItem('docflow_token');
      if (token) {
        // sendBeacon for reliable cleanup on page unload (text/plain avoids CORS preflight)
        navigator.sendBeacon(
          `${API}/api/auth/cleanup`,
          new Blob(['cleanup'], { type: 'text/plain' })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    };
  }, []);

  const checkAuth = async () => {
    const token = sessionStorage.getItem('docflow_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/api/auth/me`);
      setUser(response.data);
      startSessionTimer();
    } catch (error) {
      sessionStorage.removeItem('docflow_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const verifyKey = async (key) => {
    const response = await axios.post(`${API}/api/auth/verify-key`, { key });
    if (response.data.verified) {
      sessionStorage.setItem('docflow_key_verified', 'true');
      setKeyVerified(true);
    }
    return response.data;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/api/auth/login`, { email, password });
    const data = response.data;
    if (data.requires_2fa) {
      setPendingLogin({
        temp_token: data.temp_token,
        name: data.name,
        email: data.email
      });
      return { requires_2fa: true };
    }
    // 2FA disabled — got full access token directly
    if (data.token) {
      sessionStorage.setItem('docflow_token', data.token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      startSessionTimer();
    }
    return { requires_2fa: false };
  };

  const verify2FA = async (code) => {
    if (!pendingLogin) throw new Error('No pending login');
    const response = await axios.post(`${API}/api/auth/verify-2fa`, {
      temp_token: pendingLogin.temp_token,
      code
    });
    const data = response.data;
    if (data.token) {
      sessionStorage.setItem('docflow_token', data.token);
    }
    const userData = { id: data.id, email: data.email, name: data.name, role: data.role };
    setUser(userData);
    setPendingLogin(null);
    startSessionTimer();
    return userData;
  };

  const fullCleanupAndLogout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {});
    } catch (error) {
      // silent
    }
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    sessionStorage.clear();
    setUser(null);
    setPendingLogin(null);
    setKeyVerified(false);
  };

  const logout = async () => {
    await fullCleanupAndLogout();
  };

  return (
    <AuthContext.Provider value={{
      user, loading, keyVerified, pendingLogin,
      verifyKey, login, verify2FA, logout, checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};
